<template>
  <div id="scope-select">
    <!-- Desktop / Tablet (>= md): Tabs -->
    <div class="notification" v-if="searchError404">
      <div class="row">
        <div class="col-auto">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="currentColor"
            class="bi bi-info-square-fill" viewBox="0 0 16 16">
            <path
              d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm8.93 4.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM8 5.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2" />
          </svg>
        </div>
        <div class="col">
          <strong>Search Temporarily Unavailable</strong><br />
          We’re currently experiencing issues with our search service and are working hard to fix it as quickly as
          possible.
        </div>
      </div>
    </div>

    <div class="tabs-wrapper d-none d-lg-flex">
      <div class="scope-select-block">
        <search-scope-select-item :isActive="isActive('combined')" :routeTo="routeNameForScope('combined')"
          :routeParams="routeParamsForScope('combined')" :count="getTotalCount"
          :isLoading="isLoading" labelKey="message.search.scopeTabTitle.combined"
          :queryParams="queryParams('combined')" />
        <search-scope-select-item :isActive="isActive('dataset')" :routeTo="routeNameForScope('dataset')"
          :routeParams="routeParamsForScope('dataset')" :count="getDatasetsCount"
          :isLoading="isLoading" labelKey="message.header.navigation.data.datasets"
          :queryParams="queryParams('dataset')" />
        <search-scope-select-item :isActive="isActive('dataservice')" :routeTo="routeNameForScope('dataservice')"
        :routeParams="routeParamsForScope('dataservice')" :count="getDataservicesCount"
        :isLoading="isLoading" labelKey="message.header.navigation.data.dataservices"
        :queryParams="queryParams('dataservice')" />
        <search-scope-select-item :isActive="isActive('datasetseries')" :routeTo="routeNameForScope('datasetseries')"
          :routeParams="routeParamsForScope('datasetseries')" :count="getDatasetseriesCount"
          :isLoading="isLoading" labelKey="message.header.navigation.data.datasetseries"
          :queryParams="queryParams('datasetseries')" />
        <search-scope-select-item v-if="useCatalogs" :isActive="isActive('catalogue')"
          :routeTo="routeNameForScope('catalogue')" :routeParams="routeParamsForScope('catalogue')"
          :count="getCatalogsCount" :isLoading="isLoading" labelKey="message.header.navigation.data.catalogs"
          :queryParams="queryParams('catalogue')" />
        <search-scope-select-item :isActive="isActive('editorialContent')"
          :routeTo="routeNameForScope('editorialContent')" :routeParams="routeParamsForScope('editorialContent')"
          :count="getEditorialContentCount" :isLoading="isLoading" labelKey="message.searchTabs.editorialContent"
          :queryParams="queryParams('editorialContent')" />
      </div>
    </div>

    <!-- Mobile (< md): Custom Dropdown -->
    <div class="d-block d-lg-none mobile-dropdown" ref="mobileDropdown">
      <button type="button" class="mobile-trigger" @click="open = !open" :aria-expanded="open.toString()"
        aria-haspopup="listbox">
        <span class="trigger-label">{{ $t(currentOption.labelKey) }}</span>
        <span class="count-badge">{{ fmt(currentOption.count) }}</span>
        <span class="divider"></span>
        <span class="arrow" :class="{ open }"></span>
      </button>
      <ul v-if="open" class="mobile-menu" role="listbox" :aria-activedescendant="selectedScope">
        <li v-for="opt in options" :key="opt.value" :id="opt.value" role="option"
          :aria-selected="opt.value === selectedScope">
          <button type="button" @click="select(opt.value)" :disabled="!isSelectable(opt.value)"
            :class="{ disabled: !isSelectable(opt.value) }">
            <span class="opt-label">{{ $t(opt.labelKey) }}</span>
            <span class="badge-inline">{{ fmt(opt.count) }}</span>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import SearchScopeSelectItem from "@/components/search/search-filters/SearchScopeSelectItem";
export default {
  name: "SearchScopeSelectBar",
  components: { SearchScopeSelectItem },
  props: { config: Object },
  data() {
    return {
      searchError404: false,
      useCatalogs: this.$env.content.datasets.useCatalogs,
      textKeys: {
        dataset: "message.header.navigation.data.datasets",
        catalogue: "message.header.navigation.data.catalogs",
        editorialContent: "message.searchTabs.editorialContent",
      },
      getDatasetsCount: 0,
      getEditorialContentCount: 0,
      getTotalCount: 0,
      getDataserviceCount: 0,
      getDataservicesCount: 0,
      getDatasetseriesCount: 0,
      getCatalogsCount: 0,
      isLoading: false,
      selectedScope: this.$route.query.searchScope || this.config.scope,
      routeMap: {
        combined: "Combined",
        dataset: "Datasets",
        dataservice: "Dataservices",
        datasetseries: "datasetseries",
        catalogue: "Catalogues",
        editorialContent: "EditorialContent"
      },
      open: false
    }
  },
  computed: {
    getCount() {
      return this.$store.getters[`${this.config.getCount[0]}/${this.config.getCount[1]}`];
    },
    options() {
      const base = [
        { value: 'combined', labelKey: 'message.search.scopeTabTitle.combined', count: this.getTotalCount },
        { value: 'dataset', labelKey: 'message.header.navigation.data.datasets', count: this.getDatasetsCount },
      ];
      base.push({ value: 'dataservice', labelKey: 'message.header.navigation.data.dataservices', count: this.getDataservicesCount });
      base.push({ value: 'datasetseries', labelKey: 'message.header.navigation.data.datasetseries', count: this.getDatasetseriesCount });
      if (this.useCatalogs) {
        base.push({ value: 'catalogue', labelKey: 'message.header.navigation.data.catalogs', count: this.getCatalogsCount });
      }
      base.push({ value: 'editorialContent', labelKey: 'message.searchTabs.editorialContent', count: this.getEditorialContentCount });
      return base;
    },
    currentOption() {
      return this.options.find(o => o.value === this.selectedScope) || this.options[0];
    },
    getSortType() {
      return this.$store.getters['search/getSortType'];
    },
    getMostLikedTotal() {
      return this.$store.getters['search/getMostLikedTotal'];
    },
    isInCatalogueDetailsView() {
      const isCatalogueDetailsRoute = this.$route.name === 'CatalogueDetails';
      const hasCatalogueDetailsComponent = this.$route.matched?.some((record) =>
        Object.values(record.components || {}).some((component) => component?.name === 'BayernCatalogPage')
      );

      return isCatalogueDetailsRoute || hasCatalogueDetailsComponent;
    },
  },
  watch: {
    '$route.query': {
      handler() { this.countRequest(); },
      deep: true
    },
    '$route.query.searchScope'(v) {
      this.selectedScope = this.resolveSelectedScope(v);
    },
    'config.scope'() {
      if (!this.$route.query.searchScope) {
        this.selectedScope = this.resolveSelectedScope();
      }
    },
    getMostLikedTotal(newVal) {
      if (this.getSortType === "mostLiked") {
        this.getTotalCount = newVal
        this.getDatasetsCount = newVal
        this.setDataserviceCount(newVal)
      }
    },
  },
  methods: {
    resolveSelectedScope(routeScope = this.$route?.query?.searchScope) {
      if (typeof routeScope === 'string' && this.routeMap[routeScope]) {
        return routeScope;
      }
      return this.config.scope;
    },
    normalizeQueryFacetValue(value) {
      if (value == null) return [];
      return (Array.isArray(value) ? value : [value]).filter(
        (entry) => entry !== null && entry !== undefined && entry !== ""
      );
    },
    syncFacetFromRoute(facets, fieldName, routeQuery) {
      const values = this.normalizeQueryFacetValue(routeQuery[fieldName]);
      if (values.length > 0) facets[fieldName] = values;
      else delete facets[fieldName];
    },
    hasActiveHvdCategorySelection() {
      return this.normalizeQueryFacetValue(this.$route.query?.hvdCategory).length > 0;
    },
    isSelectable(scope) {
      if (scope === "dataservice" && this.hasActiveHvdCategorySelection()) {
        return false;
      }
      const option = this.options.find((o) => o.value === scope);
      return !!option && (option.count || 0) > 0;
    },
    setDataserviceCount(value) {
      this.getDataserviceCount = value;
      this.getDataservicesCount = value;
    },
    routeNameForScope(scope) {
      if (!this.isInCatalogueDetailsView) {
        return this.routeMap[scope];
      }
      return this.$route.name === 'CatalogueDetails' ? 'CatalogueDetails' : 'Catalogues';
    },
    routeParamsForScope(scope) {
      if (!scope) {
        return undefined;
      }
      if (this.$route.name !== 'CatalogueDetails' || !this.$route.params?.ctlg_id) {
        return undefined;
      }
      return { ctlg_id: this.$route.params.ctlg_id };
    },
    isActive(scope) { return scope === this.selectedScope; },
    queryParams(scope) {
      const qp = {};
      if (this.$route.query.superCatalog === "erpd" ||
        (this.$route.query.superCatalog?.length > 0 && this.$route.query.superCatalog[0] === "erpd")) {
        if (['catalogue', 'dataset', 'dataservice', 'datasetseries', 'combined'].includes(scope)) qp.superCatalog = "erpd";
      }
      if (this.$route.query) qp.query = { ...this.$route.query };
      if (this.$route.query?.mostLiked === "true"){
        qp.query = {
          mostLiked: true,
          page: this.$route.query.page,
          sort: "mostLiked",
          locale: this.$route.query.locale
        }
        this.$router.replace({
          query: qp.query
        });
      }

      qp.query = { ...(qp.query || {}), page: 1, searchScope: scope };
      
      return qp;
    },
    select(scope) {
      if (!this.isSelectable(scope)) return;
      this.selectedScope = scope;
      this.open = false;
      this.$router.push({
        name: this.routeNameForScope(scope),
        params: this.routeParamsForScope(scope),
        query: this.queryParams(scope)
      });
    },
    fmt(v) { return new Intl.NumberFormat('de-DE').format(v || 0); },
    handleClickOutside(e) {
      if (this.open && this.$refs.mobileDropdown && !this.$refs.mobileDropdown.contains(e.target)) this.open = false;
    },
    async countRequest() {
      this.isLoading = true;
      const baseUrl = this.$env.api.baseUrl;
      const UrlQuery = this.$route.query;
      const includes = this.config.apiLinkIncludes;

      // Get facets and exclude dataServices from them
      const allFacets = this.$store.getters[`${this.config.getFacets[0]}/${this.config.getFacets[1]}`] || {};
      const { ...facetsWithoutDataServices } = allFacets;

      // HVD selections can be route-driven via ECTreeSelect and may temporarily
      // not be reflected in store facets yet. Keep count facets aligned to URL.
      this.syncFacetFromRoute(facetsWithoutDataServices, 'is_hvd', UrlQuery);
      this.syncFacetFromRoute(facetsWithoutDataServices, 'hvdCategory', UrlQuery);

      const encodedFacets = encodeURIComponent(JSON.stringify(facetsWithoutDataServices));
      const readableFacets = encodedFacets
        .replace(/%3A/g, ':').replace(/%5B/g, '[')
        .replace(/%5D/g, ']').replace(/%2C/g, ',');

      let dateParams = '';
      if (UrlQuery.minDate) dateParams += `&minDate=${UrlQuery.minDate}`;
      if (UrlQuery.maxDate) dateParams += `&maxDate=${UrlQuery.maxDate}`;
      if (UrlQuery.dateType) dateParams += `&dateType=${UrlQuery.dateType}`;

      // Add scoring parameter (metadata quality filter)
      let scoringParams = '';
      const facets = facetsWithoutDataServices;
      if (facets.scoring && facets.scoring.length > 0) {
        if (facets.scoring.includes("excellentScoring")) {
          scoringParams = "&minScoring=351";
        } else if (facets.scoring.includes("goodScoring")) {
          scoringParams = "&minScoring=221&maxScoring=350";
        } else if (facets.scoring.includes("sufficientScoring")) {
          scoringParams = "&minScoring=121&maxScoring=220";
        }
      }

      const fieldsParam = UrlQuery.fields ? `&fields=${UrlQuery.fields}` : '';
      const filter = "&filters=catalogue,dataset,dataservice,datasetseries,resource";
      const dataServicesParam = (UrlQuery.dataServices === 'true' || this.$store.getters['datasets/getDataServices'] === 'true')
        ? "&dataServices=true"
        : "";
      const params = "&resource=editorial-content&countOnly=true";
      // const params = "&resource=editorial-content";
      const q = UrlQuery.query ? UrlQuery.query : "";
      const url = `${baseUrl}search?q=${q}${filter}${params}${dateParams}${scoringParams}${fieldsParam}${dataServicesParam}${includes ? "&includes=" + includes : ""}&facets=${readableFacets}`;

     try {
      const r = await fetch(url);
      
      // Check for 404 status
      if (r.status === 404) {
        this.searchError404 = true;
        return;
      }
      
      if (!r.ok) {
        throw new Error(`HTTP error! status: ${r.status}`);
      }
      
      const d = await r.json();

      const c = d.result?.count || {};
      this.getDatasetsCount = c.dataset || 0;
      this.setDataserviceCount(c.dataservice || 0);
      this.getDatasetseriesCount = c.series || c.datasetseries || 0;
      this.getEditorialContentCount = c['resource_editorial-content'] || 0;
      this.getCatalogsCount = c.catalogue || 0;
      this.getTotalCount = c.total || 0;

      // HVD category tree selection yields no dataservice results.
      if (this.hasActiveHvdCategorySelection()) {
        this.setDataserviceCount(0);
      }

      if (this.getSortType === "mostLiked") {
        this.getTotalCount = this.getMostLikedTotal
        this.getDatasetsCount = this.getMostLikedTotal
      }
    } catch (e) {
      console.error('Count request failed:', e);
      this.getDatasetsCount = 0;
      this.setDataserviceCount(0);
      this.getDatasetseriesCount = 0;
      this.getEditorialContentCount = 0;
      this.getCatalogsCount = 0;
      this.getTotalCount = 0;
      
      // Optionally set error state for network errors too
      this.searchError404 = true;
    } finally {
      this.isLoading = false;
    }
},
  },
  mounted() {
    this.countRequest();
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  }
}
</script>

<style lang="scss" scoped>
.notification {
  margin-left: 12px;
  margin-bottom: 20px;
  padding: 20px 20px 20px 20px;
  background-color: #f3f6fc;
  border-left: 2px solid #0e47cb;

}

.notification svg {
  margin-right: -5px;
  vertical-align: middle;
  color: #0e47cb;
  margin-bottom: 0px;
}

#scope-select {
  margin-bottom: 2rem;
  width: 100%;
}

/* Desktop Tabs Wrapper */
.tabs-wrapper {

  >div {
    // border: 1px solid #aaa;
    margin-left: 13px;
    display: flex;
    flex-direction: row;
    list-style-type: none;
    border-radius: 4px;
  }
}

.scope-select-block {
  display: flex;
  flex-direction: row;
}

@media screen and (max-width: 992px) {
  .scope-select-block {
    flex: 1;
    flex-direction: column;
  }
}

@media screen and (max-width: 767.5px) {
  .tabs-wrapper {
    border-color: #0e47cb;
    margin-left: 0;
  }

  #scope-select {
    margin-bottom: 10px;
    width: 100%;
  }

}

/* Mobile Dropdown */
.mobile-dropdown {
  position: relative;
  width: 100%;
}

.mobile-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  gap: .75rem;
  background: #1546cf;
  color: #fff;
  border: 0;
  border-radius: 8px;
  padding: .85rem 3rem .85rem 1.25rem;
  font-weight: 600;
  cursor: pointer;
  position: relative;
  text-align: left;
}

.mobile-trigger .count-badge {
  background: #fff;
  color: #1546cf;
  font-weight: 600;
  border-radius: 999px;
  padding: .25rem .75rem;
  font-size: .85rem;
  line-height: 1;
  display: inline-block;
}

.mobile-trigger .divider {
  position: absolute;
  top: 8px;
  bottom: 8px;
  right: 48px;
  width: 1px;
  background: rgba(255, 255, 255, .65);
}

.mobile-trigger .arrow {
  position: absolute;
  right: 16px;
  width: 14px;
  height: 14px;
  transition: transform .2s;
}

.mobile-trigger .arrow::before {
  content: "";
  display: block;
  width: 100%;
  height: 100%;
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' stroke='%23000' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' fill='none'%3E%3Cpolyline points='3 5 7 9 11 5'/%3E%3C/svg%3E") center/contain no-repeat;
  background: #fff;
}

.mobile-trigger .arrow.open {
  transform: rotate(180deg);
}

.mobile-trigger:focus {
  outline: 2px solid #0e47cb;
  outline-offset: 2px;
}

.mobile-menu {
  list-style: none;
  margin: .5rem 0 0;
  padding: .25rem;
  background: #fff;
  border: 1px solid #d6dff5;
  border-radius: 8px;
  position: absolute;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 20;
  box-shadow: 0 4px 16px rgba(0, 0, 0, .12);
}

.mobile-menu li button {
  width: 100%;
  background: transparent;
  border: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: .6rem .75rem;
  font-weight: 500;
  cursor: pointer;
  border-radius: 6px;
  color: #183366;
}

.mobile-menu li button:hover,
.mobile-menu li button:focus {
  background: #f0f5ff;
  outline: none;
}

.badge-inline {
  background: #1546cf;
  color: #fff;
  border-radius: 999px;
  padding: .2rem .55rem;
  font-size: .75rem;
  font-weight: 600;
  line-height: 1;
}
</style>
